import { cn } from "@/lib/utils";
import { Empty } from "components/core/Empty";
import { Separator } from "components/core/Seperator";
import { PageTitle } from "components/core/typography/PageTitle";
import { ArchiveCard } from "components/domain/archive/ArchiveCard";
import { ContentCard } from "components/domain/contents/cards/ContentCard";
import { ContentCardSekeleton } from "components/domain/contents/cards/ContentCardSkeleton";
import {
    MixArchiveDocument,
    MixArchiveTagsDocument,
    SearchArchiveDocument,
    SearchDocument,
    useMixArchiveBookmarkedIdsQuery,
    useMixArchiveQuery,
    useMixArchiveTagsQuery,
    useSearchArchiveQuery,
    useSearchQuery,
    useSessionQuery
} from "graphql/generated";
import { useAuth } from "hooks/useAuth";
import { useHitBottom } from "hooks/useHitBottom";
import { fireEvent } from "lib/ga";
import _ from "lodash";
import { GetServerSideProps } from "next";
import { useEffect, useState } from "react";
import React, { useCallback, useMemo } from "react";
import { slidein } from "styles/animation/slidein";
import { ContentSection } from "types/app";
import { getClient } from "utils/client";
import { SHINHAN_INVEST_TAG_LIST } from "utils/enum";
import { isNotNil } from "utils/isNotNil";

interface PageProps {
    initText: string;
}

const LIMIT = 20;
const OFFSET = 0;

const SearchPage = ({ initText }: PageProps) => {
    const { data: sessionData } = useSessionQuery();
    const { me, openSignInModal } = useAuth();
    const { data: tagsData } = useMixArchiveTagsQuery();

    const userGroup = sessionData?.session?.me?.group_id;
    const shouldHideShinhanOnly =
        userGroup === "general" ||
        userGroup === "thesmc" ||
        userGroup === undefined;

    // Query ALL archive data (works for unauthorized users) and filter client-side
    // Using mixArchive instead of searchArchive because it has public access permissions
    const { data: allArchivesData, loading: archiveLoading } =
        useMixArchiveQuery({
            variables: {
                limit: 1000, // Get enough archives to search through
                offset: 0,
                orderBy: [{ created_at: "desc" as any }]
            },
            fetchPolicy: "cache-and-network"
        });

    // Filter archives client-side based on search text
    const archiveData = useMemo(() => {
        if (!allArchivesData?.mix_archive) return null;

        const searchLower = initText.toLowerCase();
        const filtered = allArchivesData.mix_archive.filter((archive) => {
            if (!archive) return false;
            const titleMatch = archive.title
                ?.toLowerCase()
                .includes(searchLower);
            const sourceMatch = archive.source
                ?.toLowerCase()
                .includes(searchLower);
            const sourceNameMatch = archive.sourcename
                ?.toLowerCase()
                .includes(searchLower);
            return titleMatch || sourceMatch || sourceNameMatch;
        });

        return { mix_archive: filtered.slice(0, LIMIT) };
    }, [allArchivesData, initText]);

    // Fetch user's bookmarked archive IDs
    const { data: bookmarkedData } = useMixArchiveBookmarkedIdsQuery({
        variables: {
            userId: sessionData?.session?.me?.id || ""
        },
        skip: !sessionData?.session?.me?.id
    });

    // Create a Set of bookmarked archive IDs for quick lookup
    const bookmarkedArchiveIds = useMemo(
        () =>
            new Set(
                bookmarkedData?.mix_archive_bookmark.map((b) => b.archiveid) ||
                    []
            ),
        [bookmarkedData]
    );

    // Combine archives and contents into a unified result set
    const archiveResults = useMemo(() => {
        const archives = archiveData?.mix_archive?.filter(isNotNil) || [];
        return archives.map((archive) => ({
            ...archive,
            bookmarked: bookmarkedArchiveIds.has(archive.id),
            resultType: "archive" as const
        }));
    }, [archiveData, bookmarkedArchiveIds]);

    // Query content results
    const {
        data: contentData,
        loading: contentLoading,
        fetchMore
    } = useSearchQuery({
        fetchPolicy: "cache-and-network",
        notifyOnNetworkStatusChange: true,
        variables: {
            text: `%${initText}%`,
            limit: LIMIT,
            offset: 0
        }
    });

    const contentResults = useMemo(() => {
        if (!contentData) return [];

        if (!shouldHideShinhanOnly) {
            return contentData.mix_content.filter(isNotNil);
        }

        return contentData.mix_content.filter((item) => {
            if (!isNotNil(item)) return false;

            return !(item.mix_content_tags ?? []).some((t) => {
                const value = t?.mix_content_tag_enum?.value;
                return (
                    isNotNil(value) &&
                    (SHINHAN_INVEST_TAG_LIST as string[]).includes(
                        value as string
                    )
                );
            });
        });
    }, [contentData, shouldHideShinhanOnly]);

    const [reachEnd, setReachEnd] = useState(false);

    const tagEnums = useMemo(
        () => tagsData?.mix_archive_tags || [],
        [tagsData]
    );

    const fetchMoreContent = useCallback(() => {
        if (!contentLoading && !reachEnd) {
            fetchMore({
                variables: {
                    text: `%${initText}%`,
                    offset: contentResults.length
                },
                updateQuery: (previousQueryResult, { fetchMoreResult }) => {
                    return {
                        mix_content: _.uniqBy(
                            [
                                ...previousQueryResult.mix_content,
                                ...fetchMoreResult.mix_content
                            ],
                            (item) => item.id
                        )
                    };
                }
            }).then((response) => {
                const newContent =
                    response.data?.mix_content.filter(isNotNil) || [];
                if (newContent.length === 0) {
                    setReachEnd(true);
                }
            });
        }
    }, [contentLoading, reachEnd, fetchMore, initText, contentResults.length]);

    useEffect(() => {
        setReachEnd(false);
    }, [initText]);

    useHitBottom({ fetch: fetchMoreContent });

    const handleArchiveClick = useCallback(
        (archive: (typeof archiveResults)[0]) => {
            if (!me) {
                openSignInModal();
                return;
            }
            if (archive.view_url) {
                window.open(archive.view_url, "_blank");
            }
        },
        [me, openSignInModal]
    );

    const showEmpty =
        !archiveLoading &&
        !contentLoading &&
        archiveResults.length === 0 &&
        contentResults.length === 0;
    const showSkeleton =
        (archiveLoading || contentLoading) &&
        archiveResults.length === 0 &&
        contentResults.length === 0;

    return (
        <div
            className="mt-[calc(var(--searchbar-height)-var(--searchbar-padding-bottom))]
                flex w-full flex-col md:mt-0"
        >
            <PageTitle className="hidden text-text-placeholder md:block">
                <span className="text-secondary-main">{initText}</span> 관련
                콘텐츠
            </PageTitle>

            {/* Unified Results Section - Archives + Content inline */}
            <div
                className={cn(
                    "h-full w-full",
                    slidein,
                    showEmpty
                        ? "flex items-center justify-center"
                        : `flex flex-col gap-y-6 md:grid
                            md:grid-cols-[repeat(auto-fill,minmax(270px,1fr))]
                            md:gap-x-6 md:gap-y-16`
                )}
            >
                {showEmpty ? (
                    <Empty
                        imgurl="/img/search-empty.png"
                        mainText={`${initText}에 대한 검색 결과가 없습니다.`}
                        subText="다른 단어로 다시 검색해 보세요!"
                    />
                ) : showSkeleton ? (
                    <>
                        <ContentCardSekeleton />
                        <ContentCardSekeleton />
                        <ContentCardSekeleton />
                        <ContentCardSekeleton />
                        <ContentCardSekeleton />
                        <ContentCardSekeleton />
                    </>
                ) : (
                    <>
                        {/* Render archive results first */}
                        {archiveResults.map((archive) => (
                            <React.Fragment key={`archive-${archive.id}`}>
                                <ArchiveCard
                                    archive={archive}
                                    tagEnums={tagEnums}
                                    onClick={() => handleArchiveClick(archive)}
                                    isGuest={!me}
                                />
                                <Separator
                                    className="mx-[calc(-1*var(--page-padding))]
                                        flex w-auto md:hidden"
                                />
                            </React.Fragment>
                        ))}

                        {/* Render content results after archives */}
                        {contentResults.map((content) => (
                            <React.Fragment key={content.id}>
                                <ContentCard
                                    type={
                                        content.isoriginal
                                            ? content.released
                                                ? "original"
                                                : "upcoming"
                                            : "curation"
                                    }
                                    content={content}
                                    onClick={() => {
                                        const params = {
                                            content_section:
                                                "search" as ContentSection,
                                            content_url: content.originalurl,
                                            content_isoriginal:
                                                content.isoriginal,
                                            content_released: content.released
                                        };

                                        fireEvent({
                                            event: "content_click",
                                            params: {
                                                content_id: content.id,
                                                content_title: content.title,
                                                ...params
                                            }
                                        });
                                    }}
                                />
                                <Separator
                                    className="mx-[calc(-1*var(--page-padding))]
                                        flex w-auto md:hidden"
                                />
                            </React.Fragment>
                        ))}
                    </>
                )}
            </div>
        </div>
    );
};

export const getServerSideProps: GetServerSideProps<PageProps> = async (
    ctx
) => {
    const client = getClient({
        headers: {
            cookie: ctx.req.headers.cookie
        },
        ctx
    });

    const initText = typeof ctx.query.text === "string" ? ctx.query.text : "";

    try {
        // Prefetch content, archive search results, and tags
        await Promise.all([
            client.query({
                query: SearchDocument,
                variables: {
                    text: `%${initText}%`,
                    limit: LIMIT,
                    offset: OFFSET
                }
            }),
            client.query({
                query: MixArchiveDocument,
                variables: {
                    limit: 1000,
                    offset: 0,
                    orderBy: [{ created_at: "desc" }]
                }
            }),
            client.query({
                query: MixArchiveTagsDocument
            })
        ]);
        return {
            props: {
                initText,
                __APOLLO_CACHE_DATA__: client.extract()
            }
        };
    } catch (error) {
        return {
            props: {
                initText,
                __APOLLO_CACHE_DATA__: client.extract()
            }
        };
    }
};

export default SearchPage;
